import React from 'react';
import { object } from 'prop-types';

// import { staticExchangeRates } from '../../config/configStripe';
import {
  FormattedMessage,
  intlShape,
  injectIntl,
} from '../../../../util/reactIntl';
import { displayPrice } from '../../../../util/configHelpers';
import { lazyLoadWithDimensions } from '../../../../util/uiHelpers';
import { convertPrice, formatMoney } from '../../../../util/currency';
import { createSlug } from '../../../../util/urlHelpers';
import { isBookingProcessAlias } from '../../../../transactions/transaction';

import {
  AspectRatioWrapper,
  Modal,
  NamedLink,
  ResponsiveImage,
} from '../../../../components';

import IconCard from '../../../../components/SavedCardDetails/IconCard/IconCard';
import { types as sdkTypes } from 'sharetribe-flex-sdk';
import { fetchMonthlyTimeSlots } from '../../../ListingPage/ListingPage.duck';
import { transformItemsToOriginalListings } from '../../StoreProfilePage';
import css from './RentalCard.module.css';


const { Money, UUID } = sdkTypes;

const TWENTY_EIGHT_DAYS = 'twentyEightDaysPrice';
const WEEK_PRICE = 'oneWeekPrice';


const priceData = (price, currency, intl) => {
  if (price && price.currency === currency) {
    const formattedPrice = formatMoney(intl, price);
    return { formattedPrice, priceTitle: formattedPrice };
  } else if (price) {
    return {
      formattedPrice: intl.formatMessage(
        { id: 'ListingCard.unsupportedPrice' },
        { currency: price.currency }
      ),
      priceTitle: intl.formatMessage(
        { id: 'ListingCard.unsupportedPriceTitle' },
        { currency: price.currency }
      ),
    };
  }
  return {};
};

const LazyImage = lazyLoadWithDimensions(ResponsiveImage, {
  loadAfterInitialRendering: 3000,
});

const PriceMaybe = props => {
  const {
    price,
    listingType,
    defaultPriceKey,
    transactionProcessAlias,
    config,
    intl,
    selectedCurrency,
  } = props;

  const validListingTypes = config.listing.listingTypes;
  const foundListingTypeConfig = validListingTypes.find(
    conf => conf.listingType === listingType
  );
  const showPrice = displayPrice(foundListingTypeConfig);
  if (!showPrice && price) {
    return null;
  }

  const isBookable = isBookingProcessAlias(transactionProcessAlias);
  const { formattedPrice, priceTitle } = priceData(
    price,
    selectedCurrency,
    intl
  );

  return (
    <div className={css.price}>
      <div className={css.priceValue} title={priceTitle}>
        {formattedPrice}
      </div>
      {isBookable ? (
        <div className={css.perUnit}>
          <FormattedMessage
            id="ListingCard.perUnit"
            values={{ defaultPriceKey: defaultPriceKey || 'perDayPrice' }}
          />
        </div>
      ) : null}
    </div>
  );
};

function truncateText(text, maxLength) {
  if (text.length <= maxLength) {
    return text; // Return the original text if it's within or equal to the maxLength
  } else {
    return text.slice(0, maxLength) + '...'; // Truncate the text and add ellipsis
  }
};

export const RentalCardComponent = props => {
  const {
    rental = {},
    intl,
    handleDrag,
    handleDrop,
    handleDragEnd,
    allowDrag,
    config,
    setCartModalId,
    exchangeRates,
    currentUserCurrency,
    isCurrentUser,
    dispatch
  } = props;

  const id = rental.objectID;
  const {
    title = '',
    price,
    user: { displayName },
    publicData,
  } = rental;

  const {
    category,
    city,
    listingType,
    defaultPriceKey,
    transactionProcessAlias,
    listingImages = [],
    prices = {},
  } = publicData || {};

  const monthPrice = prices[TWENTY_EIGHT_DAYS];
  const weekPrice = prices[WEEK_PRICE];

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;

  const slug = createSlug(title);

  const convertedPrice = !!price
    ? convertPrice(
      exchangeRates,
      price.amount,
      price.currency,
      currentUserCurrency
    )
    : null;
  const convertedMonthPrice = !!monthPrice
    ? convertPrice(
      exchangeRates,
      monthPrice.amount,
      monthPrice.currency,
      currentUserCurrency
    )
    : null;
  const convertedWeeklyPrice = !!weekPrice
    ? convertPrice(
      exchangeRates,
      weekPrice.amount,
      weekPrice.currency,
      currentUserCurrency
    )
    : null;

  const formattedMonthPrice = convertedMonthPrice
    ? formatMoney(
      intl,
      new Money(convertedMonthPrice.amount, convertedMonthPrice.currency)
    )
    : null;
  const formattedWeekPrice = convertedWeeklyPrice
    ? formatMoney(
      intl,
      new Money(convertedWeeklyPrice.amount, convertedWeeklyPrice.currency)
    )
    : null;

  const queryString =
    !!category && !!city
      ? `?city=${city.toLowerCase()}&category=${category.toLowerCase()}`
      : !!category
        ? `?category=${category.toLowerCase()}`
        : !!city
          ? `?city=${city.toLowerCase()}`
          : null;

  const firstImage = listingImages[0];
  const firstImageMaybe =
    !!firstImage && firstImage.secure_url ? (
      <NamedLink
        name="ListingPage"
        params={{ id, slug }}
        to={{ search: queryString }}
      >
        <ResponsiveImage
          className={css.imageWrapper}
          renderCloudinaryImage={{
            secure_url: firstImage.secure_url,
            alt: title,
            variantsToRender: [
              'listing-card-4x',
              'listing-card-2x',
              'listing-card',
            ],
            defaultVariant: 'listing-card-4x',
          }}
        />
      </NamedLink>
    ) : (
      <NamedLink
        name="ListingPage"
        params={{ id, slug }}
        to={{ search: queryString }}
      >
        <div className={css.noImageWrapper}>
          <svg
            color="#333"
            width="100px"
            height="100px"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M7.828 5l-1-1H22v15.172l-1-1v-.69l-3.116-3.117-.395.296-.714-.714.854-.64a.503.503 0 0 1 .657.046L21 16.067V5zM3 20v-.519l2.947-2.947a1.506 1.506 0 0 0 .677.163 1.403 1.403 0 0 0 .997-.415l2.916-2.916-.706-.707-2.916 2.916a.474.474 0 0 1-.678-.048.503.503 0 0 0-.704.007L3 18.067V5.828l-1-1V21h16.172l-1-1zM17 8.5A1.5 1.5 0 1 1 15.5 7 1.5 1.5 0 0 1 17 8.5zm-1 0a.5.5 0 1 0-.5.5.5.5 0 0 0 .5-.5zm5.646 13.854l.707-.707-20-20-.707.707z" />
            <path fill="none" d="M0 0h24v24H0z" />
          </svg>
        </div>
      </NamedLink>
    );

  const MorePrices = props => {
    const { formattedWeekPrice, formattedMonthPrice } = props;

    const weekPrice = formattedWeekPrice ? (
      <span>{`${formattedWeekPrice}/Week`}</span>
    ) : null;
    const monthPrice = formattedMonthPrice ? (
      <span>{`${formattedMonthPrice}/Month`}</span>
    ) : null;

    return (
      <>
        <div className={css.morePricesDesktop}>
          {weekPrice}
          {monthPrice}
        </div>
        <div className={css.morePricesMobile}>{intl.formatMessage({id:"RentalCard.seeMorePrices"})}</div>
      </>
    );
  };

  const draggablePropsMaybe = allowDrag
    ? {
      draggable: true,
      onDragOver: ev => ev.preventDefault(),
      onDragStart: handleDrag,
      onDragEnd: handleDragEnd,
      onDrop: handleDrop,
    }
    : {};

  return (
    <>
      <div className={css.rentalCard} id={id} {...draggablePropsMaybe} onContextMenu={e => e.preventDefault()} onCopy={() => false}>
        {allowDrag ? (
          <div className={css.dragBtn}>
            <IconCard brand="drag" />
          </div>
        ) : null}
        <AspectRatioWrapper
          className={css.aspectRatioWrapper}
          width={aspectWidth}
          height={aspectHeight}
        >
          {firstImageMaybe}
        </AspectRatioWrapper>
        <div className={css.info}>
          <div className={css.mainInfo}>
            <div className={css.title}>{truncateText(title, 43)}</div>
          </div>
          <span className={css.border} />
          <PriceMaybe
            price={convertedPrice}
            listingType={listingType}
            transactionProcessAlias={transactionProcessAlias}
            defaultPriceKey={defaultPriceKey}
            config={config}
            intl={intl}
            selectedCurrency={currentUserCurrency}
          />
          <MorePrices
            formattedWeekPrice={formattedWeekPrice}
            formattedMonthPrice={formattedMonthPrice}
          />
          {!isCurrentUser && typeof setCartModalId == 'function' ? <button
            type="button"
            className={css.addToCart}
            onClick={() => {
              setCartModalId(id)
              fetchMonthlyTimeSlots(dispatch, transformItemsToOriginalListings([rental])[0])
            }}>
            <IconCard brand="plus" />
          </button> : null}
        </div>
      </div>
    </>
  );
};

RentalCardComponent.propTypes = {
  intl: intlShape.isRequired,
  rental: object.isRequired,
};

export default injectIntl(RentalCardComponent);
